type RawSqlFn = (sql: string, params?: unknown[]) => Promise<Array<Record<string, unknown>>>;

export type AdminTableSchema = {
  tableName: string;
  quotedTable: string;
  columns: Set<string>;
};

export type AdminSqlStatement = {
  sql: string;
  params?: unknown[];
};

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function isAdminIdentifier(value: unknown): value is string {
  return typeof value === "string" && IDENTIFIER_RE.test(value);
}

export function validateAdminIdentifier(value: unknown, kind: string): string {
  if (!isAdminIdentifier(value)) {
    throw new Error(`Invalid ${kind} identifier`);
  }
  return value;
}

export function quoteAdminIdentifier(identifier: string): string {
  return `"${validateAdminIdentifier(identifier, "SQL")}"`;
}

export async function loadAdminTableSchema(rawSql: RawSqlFn, table: string): Promise<AdminTableSchema> {
  const tableName = validateAdminIdentifier(table, "table");
  const rows = await rawSql(
    `SELECT column_name
     FROM information_schema.columns
     WHERE table_schema = $1 AND table_name = $2
     ORDER BY ordinal_position`,
    ["public", tableName],
  );
  const columns = new Set(rows.map((row) => row.column_name).filter(isAdminIdentifier));
  if (columns.size === 0) {
    throw new Error(`Unknown table: ${tableName}`);
  }
  return {
    tableName,
    quotedTable: quoteAdminIdentifier(tableName),
    columns,
  };
}

export function resolveAdminColumn(schema: AdminTableSchema, column: string, kind = "column"): string {
  const columnName = validateAdminIdentifier(column, kind);
  if (!schema.columns.has(columnName)) {
    throw new Error(`Unknown ${kind}: ${columnName}`);
  }
  return quoteAdminIdentifier(columnName);
}

export function normalizeAdminLimit(value: string | undefined): number {
  const parsed = Number.parseInt(value || "50", 10);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(200, Math.max(1, parsed));
}

export function normalizeAdminOffset(value: string | undefined): number {
  const parsed = Number.parseInt(value || "0", 10);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, parsed);
}

export function normalizeAdminOrder(value: string | undefined): "ASC" | "DESC" {
  return value?.toLowerCase() === "asc" ? "ASC" : "DESC";
}

export function assertAdminWriteBody(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new Error("Request body must be an object");
  }
  return body as Record<string, unknown>;
}

export function buildAdminSelectStatement(params: {
  schema: AdminTableSchema;
  orderBy: string;
  order: string | undefined;
  limit: number;
  offset: number;
}): AdminSqlStatement {
  const orderColumn = resolveAdminColumn(params.schema, params.orderBy, "orderBy");
  return {
    sql: `SELECT * FROM ${params.schema.quotedTable} ORDER BY ${orderColumn} ${normalizeAdminOrder(params.order)} LIMIT $1 OFFSET $2`,
    params: [params.limit, params.offset],
  };
}

export function buildAdminCountStatement(schema: AdminTableSchema): AdminSqlStatement {
  return { sql: `SELECT COUNT(*) as count FROM ${schema.quotedTable}` };
}

export function buildAdminInsertStatement(
  schema: AdminTableSchema,
  body: Record<string, unknown>,
): AdminSqlStatement {
  const keys = Object.keys(body);
  if (keys.length === 0) {
    throw new Error("Insert body must include at least one column");
  }
  const columns = keys.map((key) => resolveAdminColumn(schema, key)).join(", ");
  const placeholders = keys.map((_, index) => `$${index + 1}`).join(", ");
  return {
    sql: `INSERT INTO ${schema.quotedTable} (${columns}) VALUES (${placeholders}) RETURNING *`,
    params: keys.map((key) => body[key]),
  };
}

export function buildAdminUpdateStatement(
  schema: AdminTableSchema,
  body: Record<string, unknown>,
  id: string,
): AdminSqlStatement {
  const keys = Object.keys(body);
  if (keys.length === 0) {
    throw new Error("Update body must include at least one column");
  }
  const sets = keys.map((key, index) => `${resolveAdminColumn(schema, key)} = $${index + 1}`).join(", ");
  const idColumn = resolveAdminColumn(schema, "id", "id column");
  return {
    sql: `UPDATE ${schema.quotedTable} SET ${sets} WHERE ${idColumn} = $${keys.length + 1} RETURNING *`,
    params: [...keys.map((key) => body[key]), id],
  };
}

export function buildAdminDeleteStatement(schema: AdminTableSchema, id: string): AdminSqlStatement {
  const idColumn = resolveAdminColumn(schema, "id", "id column");
  return {
    sql: `DELETE FROM ${schema.quotedTable} WHERE ${idColumn} = $1`,
    params: [id],
  };
}
